import { useEffect, useState, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, TouchableOpacity, ImageBackground } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { MotiView } from "moti";
import {
  useCompleteCourseInvitationMutation,
  useGetCourseModulesLiteQuery,
  useGetFacultyByCourseIdQuery,
  useGetCourseByIdWithEnrolledUserInfoQuery,
  useGetCourseProgressInformationQuery,
} from "../graphql/generated/graphql";
import CustomProgressBar from "../components/common/general/CustomProgressBar/CustomProgressBar";
import JourneyBreadcrumbs from "../components/journey/JourneyBreadcrumbs/JourneyBreadcrumbs";
import Skeleton from "react-loading-skeleton";
import ModuleCatalogueCardContainer from "../containers/customCoursework/ModuleCatalogueCardContainer/ModuleCatalogueCardContainer";
import StudentAnnouncementContainer from "../containers/customCoursework/StudentAnnouncementContainer/StudentAnnouncementContainer";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import Divider from "../components/common/layout/Divider/Divider";
import CourseStudentTabNavigator from "../components/customCoursework/CourseStudentTabNavigator/CourseStudentTabNavigator";
import StudentCommunityContainer from "../containers/customCoursework/StudentDiscussions/StudentCommunityContainer";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { useActiveCourse } from "../contexts/ActiveCourseContext";
import { useAuth } from "../hooks/useAuth";

const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
  if (courseCode && courseCode.trim()) {
    return `${courseCode} ${courseName}`;
  }
  return courseName;
};

type CustomCourseStudentViewRouteProp = RouteProp<RootStackParamList, "Custom Course Student">;
type CustomCourseStudentViewNavigationProp = NativeStackNavigationProp<RootStackParamList, "Custom Course Student">;

const TAB_DATA = [
  { key: "modules", title: "Modules" },
  { key: "community", title: "Community" },
];

const CustomCourseStudentView = () => {
  const [activeTab, setActiveTab] = useState<string>("modules");

  const navigation = useNavigation<CustomCourseStudentViewNavigationProp>();
  const route = useRoute<CustomCourseStudentViewRouteProp>();
  const posthog = usePostHog();
  const { isMedium } = useResponsiveScreen();
  const { setActiveResource, setCourseId } = useActiveCourse();

  const { course_id, courseId: legacyCourseId, integration_id: integrationId, returnToCourseEditor } = route.params;
  const courseId = course_id ?? legacyCourseId;

  const [completeCourseInvitation] = useCompleteCourseInvitationMutation();

  const { userInfo } = useAuth();

  useEffect(() => {
    setCourseId(courseId);
  }, [courseId, setCourseId]);

  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseModulesLiteQuery({
    variables: {
      id: courseId,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: courseWithEnrollment } = useGetCourseByIdWithEnrolledUserInfoQuery({
    variables: {
      id: courseId,
    },
  });

  const { data: facultyData } = useGetFacultyByCourseIdQuery({
    variables: { courseId },
    skip: !courseId,
  });

  const { data: courseProgressInformationData, refetch: refetchProgress } = useGetCourseProgressInformationQuery({
    variables: { courseId },
    skip: !courseId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const course = courseData?.getCourseModulesLite?.data?.[0];
  const courseProgressInformation = courseProgressInformationData?.getCourseProgressInformation?.data;
  const hasFaculty = facultyData?.getFacultyByCourseId?.data?.faculties.length > 0;

  const displayCourseName = course ? formatCourseNameWithCode(course.name, course.course_code) : "";
  const thumbnailUrl: string | null = (course?.thumbnail as string) || null;

  const handleModulePress = useCallback(
    (module: { id: string; title?: string }) => {
      posthog?.capture("module_card_clicked", {
        module_id: module.id,
        course_id: courseId,
        module_title: module.title,
      });

      navigation.navigate("Custom Module Student", {
        module_id: module.id,
        course_id: courseId,
        integration_id: integrationId,
      });
    },
    [navigation, courseId, integrationId, posthog]
  );

  useFocusEffect(
    useCallback(() => {
      if (course) {
        const currentRoute = navigation.getState()?.routes[navigation.getState()?.index];
        const isChildResourceView =
          currentRoute?.name === "Custom Assignment Student" ||
          currentRoute?.name === "Custom Resource Student" ||
          currentRoute?.name === "Custom Course AITutor" ||
          currentRoute?.name === "Project";

        if (!isChildResourceView) {
          setActiveResource(null);
        }
      }
      refetch();
      refetchProgress();
    }, [course, setActiveResource, refetch, refetchProgress, navigation])
  );

  useEffect(() => {
    if (course) {
      posthog?.capture("course_view", {
        course_id: courseId,
        course_name: course.name,
        integration_id: integrationId,
      });
    }
  }, [posthog, courseId, course, integrationId]);

  useEffect(() => {
    const enrollmentData = courseWithEnrollment?.getCourseByIdWithEnrolledUserInfo?.data?.[0];
    const studentHasPendingInvite = enrollmentData?.enrolledStudentsConnection?.edges?.some(
      (edge) => edge?.properties?.pendingCourseEnrollmentInvite === true && edge?.node?.id === userInfo.userId
    );
    if (studentHasPendingInvite) {
      completeCourseInvitation({ variables: { courseId } });
    }
  }, [courseWithEnrollment, userInfo.userId, completeCourseInvitation, courseId]);

  const handleFacultyViewPress = () => {
    navigation.navigate("Faculty", {
      courseName: displayCourseName,
      courseId: courseId,
      integration_id: integrationId,
    });
  };

  const handleBackPress = () => {
    navigation.navigate("Custom School Student Courses", { integration_id: integrationId });
  };

  const handleBackToCourseEditor = () => {
    navigation.navigate("Custom Coursework Modules", { courseId });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "modules":
        return (
          <MotiView
            from={{ opacity: 0, translateY: 20 }}
            animate={{ opacity: 1, translateY: 0 }}
            delay={300}
            transition={{
              opacity: { type: "timing", duration: 600 },
              translateY: { type: "timing", duration: 600 },
            }}>
            <ModuleCatalogueCardContainer
              modules={course?.modules || []}
              moduleProgressData={courseProgressInformation?.modules}
              onModulePress={handleModulePress}
            />
          </MotiView>
        );
      case "community":
        return <StudentCommunityContainer courseId={courseId} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DefaultLayout pageHasChat={false}>
        <MotiView
          from={{ opacity: 0, translateY: 20, scale: 1 }}
          animate={{ opacity: 1, translateY: 0, scale: 1 }}
          delay={800}
          transition={{ type: "timing", duration: 600 }}>
          <View style={styles.container}>
            <Skeleton height={40} width="60%" style={{ marginBottom: 10 }} />
            <Skeleton height={20} width="80%" style={{ marginBottom: 20 }} />
            <View style={styles.gridContainer}>
              {[1, 2, 3, 4].map((idx) => (
                <Skeleton key={idx} height={280} style={{ flex: 1, margin: 8, minWidth: 250 }} />
              ))}
            </View>
          </View>
        </MotiView>
      </DefaultLayout>
    );
  }

  if (error || !course) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.container}>
          <CustomText text="Course not found or error loading data" size="m" weight="medium" />
        </View>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout pageHasChat={false}>
      <MotiView
        from={{ opacity: 0, translateY: 20, scale: 1 }}
        animate={{ opacity: 1, translateY: 0, scale: 1 }}
        delay={800}
        transition={{ type: "timing", duration: 600 }}>
        <View style={styles.container}>
          {/* Breadcrumbs */}
          <View style={styles.headerContainer}>
            {returnToCourseEditor ? (
              <TouchableOpacity
                onPress={handleBackToCourseEditor}
                accessibilityLabel="Back to Course Editor"
                accessibilityRole="button">
                <CustomText
                  useTranslationText={false}
                  size="s"
                  text="< Back to Course Editor"
                  style={styles.breadcrumbText}
                />
              </TouchableOpacity>
            ) : (
              <JourneyBreadcrumbs onPress={handleBackPress} firstItem="Courses" secondItem={displayCourseName} />
            )}
          </View>

          {/* Course Header */}
          {thumbnailUrl ? (
            <>
              {/* Banner */}
              <View style={styles.bannerWrapper}>
                <ImageBackground
                  source={{ uri: thumbnailUrl }}
                  style={styles.bannerImage}
                  // @ts-expect-error - StyleSheet types are wrong
                  imageStyle={styles.bannerImageInner}
                  accessible={false}
                />
                <View style={styles.overlayCard}>
                  <View style={styles.overlayTint} />

                  {/* Top row: title + Course Instructor */}
                  <View
                    style={[
                      styles.topRow,
                      {
                        flexDirection: isMedium ? "row" : "column",
                        alignItems: isMedium ? "center" : "flex-start",
                        justifyContent: isMedium ? "space-between" : "flex-start",
                      },
                    ]}>
                    <CustomText
                      useTranslationText={false}
                      text={displayCourseName}
                      size="m"
                      textType="display"
                      weight="ultraBold"
                      style={{ marginBottom: isMedium ? 0 : 12, flexShrink: 1 }}
                      role="heading"
                      aria-level="1"
                    />
                    {hasFaculty && (
                      <View style={{ alignSelf: isMedium ? "auto" : "flex-start" }}>
                        <CustomButton
                          text={"Course Instructor"}
                          styleType="primaryLight"
                          onPress={handleFacultyViewPress}
                          leftIcon={null}
                          size="s"
                          aria-label="Course Instructor"
                          bold={true}
                        />
                      </View>
                    )}
                  </View>
                  {/* Progress header + bar */}
                  <View style={{ width: "100%" }}>
                    <View style={styles.progressHeader}>
                      <CustomText
                        text="Course Progress"
                        size="s"
                        textType="text"
                        weight="bold"
                        role="heading"
                        aria-level={2}
                        accessibilityRole="header"
                      />
                      <CustomText
                        text={`${Math.round(courseProgressInformation?.progress || 0)}% Complete`}
                        useTranslationText={false}
                        textType="text"
                        weight="bold"
                        size="s"
                      />
                    </View>
                    <View style={{ width: "100%" }}>
                      <CustomProgressBar
                        progress={courseProgressInformation?.progress || 0}
                        color={courseProgressInformation?.progress >= 100 ? "#22C55E" : "#3E68FE"}
                        unfilledColor="#E5E7EB"
                        ariaLabel="Course Progress"
                      />
                    </View>
                  </View>
                </View>
              </View>

              <CustomSpacing size="l" />
            </>
          ) : (
            <>
              {/* Fallback (no thumbnail) */}
              <MotiView
                from={{ opacity: 0, translateY: -20 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{
                  opacity: { type: "timing", duration: 600 },
                  translateY: { type: "timing", duration: 600 },
                }}>
                <View
                  style={{
                    flexDirection: isMedium ? "row" : "column",
                    flexWrap: isMedium ? "wrap" : "nowrap",
                    justifyContent: isMedium ? "space-between" : "flex-start",
                    alignItems: isMedium ? "center" : "flex-start",
                    width: "100%",
                    gap: 12,
                  }}>
                  <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", flex: 1 }}>
                    <CustomText
                      useTranslationText={false}
                      text={displayCourseName}
                      size="xl"
                      textType="display"
                      weight="bold"
                      style={styles.courseTitle}
                      role="heading"
                      aria-level="1"
                    />
                  </View>

                  <View style={{ flexDirection: "row", gap: 12, alignItems: "center" }}>
                    {hasFaculty && (
                      <CustomButton
                        text={"Course Instructor"}
                        styleType="primaryLight"
                        onPress={handleFacultyViewPress}
                        leftIcon={null}
                        size="s"
                        aria-label="Course Instructor"
                      />
                    )}
                  </View>
                </View>

                {course.description && (
                  <>
                    <CustomSpacing type="vertical" size="s" />
                    <CustomText
                      text={course.description}
                      size="m"
                      weight="normal"
                      style={styles.courseDescription}
                      useTranslationText={false}
                      role="heading"
                      aria-level="2"
                    />
                  </>
                )}

                <CustomSpacing type="vertical" size="l" />

                <View style={styles.progressSection}>
                  <View style={styles.progressSectionHeader}>
                    <CustomText
                      text="Course Progress"
                      size="m"
                      weight="bold"
                      style={styles.progressTitle}
                      useTranslationText={false}
                      role="heading"
                      aria-level={2}
                      accessibilityRole="header"
                    />
                    <CustomText
                      text={`${Math.round(courseProgressInformation?.progress || 0)}% Complete`}
                      size="m"
                      weight="bold"
                      style={styles.progressPercentage}
                      useTranslationText={false}
                      role="heading"
                      aria-level={2}
                      accessibilityRole="header"
                    />
                  </View>
                  <CustomSpacing type="vertical" size="s" />
                  <CustomProgressBar
                    progress={courseProgressInformation?.progress || 0}
                    color={courseProgressInformation?.progress >= 100 ? "#22C55E" : "#3E68FE"}
                    unfilledColor="#E5E7EB"
                    ariaLabel={`Course progress: ${Math.round(courseProgressInformation?.progress || 0)}% complete`}
                  />
                </View>
              </MotiView>

              <CustomSpacing type="vertical" size="xxl" />
            </>
          )}

          <StudentAnnouncementContainer courseId={courseId} />

          <CustomSpacing type="vertical" size="xl" />

          {/* Tab Navigator and Content */}
          <CourseStudentTabNavigator tabData={TAB_DATA} activeTab={activeTab} onTabPress={setActiveTab} />
          <Divider />

          {renderTabContent()}
        </View>
      </MotiView>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    maxWidth: 1300,
    width: "100%",
    marginHorizontal: "auto" as unknown as number,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  headerContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  breadcrumbText: {
    color: "#6B7280",
    fontWeight: "500",
  },
  bannerWrapper: {
    width: "100%",
    marginTop: 8,
    marginBottom: 48,
    position: "relative",
  },
  bannerImage: {
    width: "100%",
    height: 260,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#e5e7eb",
  },
  bannerImageInner: {
    borderRadius: 16,
  } as const,
  overlayCard: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: -28,
    padding: 20,
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#FFFFFF",
    backgroundColor: "rgba(255,255,255,0.92)",

    // Native shadows
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 4 },
    elevation: 12,
  },
  overlayTint: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  topRow: {
    width: "100%",
    gap: 16,
    marginBottom: 12,
  },
  progressHeader: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  courseTitle: {
    color: "#111827",
    lineHeight: 40,
  },
  courseDescription: {
    color: "#6B7280",
    lineHeight: 24,
  },
  progressSection: {
    backgroundColor: "#F8FAFC",
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  progressSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  progressTitle: {
    color: "#374151",
  },
  progressPercentage: {
    color: "#1F2937",
  },
  gridContainer: {
    // @ts-expect-error - CSS Grid works on web
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
});

export default CustomCourseStudentView;
